// =============================================
// KimchiPage.jsx - 김치프리미엄 전용 페이지
// =============================================
// "/kimchi" 주소로 접속하면 이 페이지가 나와.
//
// 구성 (위에서 아래로):
//   1. PremiumList  — 코인 카드 그리드 + 더보기 버튼
//   2. ChartPanel   — 카드 클릭 시 펼쳐지는 750px 차트
//
// 핵심 개념:
//   - MARKET_CAP_ORDER: 시가총액 순서가 적힌 배열 → 코인 정렬 기준
//   - showAll: true면 전체 코인 표시, false면 처음 15개만
//   - selectedCoin: null이면 차트 숨김, 값이 있으면 해당 코인 차트 표시
// =============================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import TradingViewWidget from '../components/TradingViewWidget';

const API_URL = 'http://localhost:8000';

// =============================================
// 시가총액 정렬 — 2단계 안전망
// =============================================
// [1순위] 백엔드에서 받은 market_cap_rank (CoinGecko 실제 데이터)
// [2순위] COIN_PRIORITY 하드코딩 순위표 (CoinGecko 실패·rate limit 시 폴백)
//
// ★ 왜 2단계가 필요해?
//   market_cap_rank가 없으면 (백엔드 구버전·캐시 문제 등)
//   ?? 9999 → 모든 코인이 같은 순위 → 정렬 무효 → DOGE가 맨 앞!
//   COIN_PRIORITY가 있으면 이런 상황에서도 BTC/ETH가 항상 앞에 와.
// 시가총액 기준 상위 코인 순위 (숫자가 작을수록 앞에 표시)
const COIN_PRIORITY_MAP = {
  BTC:1, ETH:2, BNB:3, XRP:4, SOL:5, ADA:6, DOGE:7, TRX:8, AVAX:9, TON:10,
  LINK:11, DOT:12, MATIC:13, SHIB:14, LTC:15, BCH:16, UNI:17, ATOM:18, XLM:19, ICP:20,
  ETC:21, APT:22, ARB:23, OP:24, NEAR:25, HBAR:26, VET:27, FIL:28, ALGO:29, GRT:30,
  AAVE:31, EOS:32, SAND:33, MANA:34, CHZ:35, AXS:36, THETA:37, FTM:38, EGLD:39, XTZ:40,
};

// 코인 하나의 정렬 순위를 계산하는 함수
function getCoinRank(coin) {
  // 1순위: 백엔드에서 받은 market_cap_rank (유효한 값이면 사용)
  if (coin.market_cap_rank && coin.market_cap_rank < 9000) {
    return coin.market_cap_rank;
  }
  // 2순위: 하드코딩 순위표에서 찾기
  if (COIN_PRIORITY_MAP[coin.coin]) {
    return COIN_PRIORITY_MAP[coin.coin];
  }
  // 둘 다 없으면 맨 뒤
  return 9999;
}

// 처음에 보여줄 코인 개수
const INITIAL_COUNT = 15;

// 차트 봉 단위
const CHART_INTERVALS = [
  { label: '15분', value: '15' },
  { label: '1시간', value: '60' },
  { label: '4시간', value: '240' },
  { label: '일봉',  value: 'D' },
  { label: '주봉',  value: 'W' },
];


// =============================================
// 메인 페이지 컴포넌트
// =============================================
export default function KimchiPage() {
  const [kimchiData, setKimchiData] = useState(null);   // 백엔드에서 받은 데이터
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');

  // 어떤 코인 차트가 열려있는지 (null이면 닫힘)
  const [selectedCoin,  setSelectedCoin]  = useState(null);
  const [chartInterval, setChartInterval] = useState('D');

  // 더보기 상태: false = 15개만 표시, true = 전체 표시
  const [showAll, setShowAll] = useState(false);

  // 차트 영역으로 스크롤하기 위한 ref
  const chartRef = useRef(null);

  // =============================================
  // 데이터 로드 함수
  // =============================================
  const fetchKimchi = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await axios.get(`${API_URL}/api/kimchi/premium`);
      setKimchiData(res.data);
    } catch {
      setError('데이터를 불러오지 못했어. 백엔드 서버가 실행 중인지 확인해봐!');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchKimchi();
    const timer = setInterval(fetchKimchi, 60000); // 60초마다 자동 갱신
    return () => clearInterval(timer);
  }, [fetchKimchi]);

  // =============================================
  // 시가총액 순 정렬 (2단계 안전망 적용)
  // =============================================
  // getCoinRank()가 backend rank → COIN_PRIORITY_MAP → 9999 순으로 판단해.
  // 이 덕분에 백엔드가 어떤 상태여도 BTC→ETH 순서가 보장돼!
  const sortedCoins = kimchiData?.coins
    ? [...kimchiData.coins].sort((a, b) => getCoinRank(a) - getCoinRank(b))
    : [];

  // showAll이 false면 앞 15개만, true면 전체
  const visibleCoins = showAll ? sortedCoins : sortedCoins.slice(0, INITIAL_COUNT);

  // =============================================
  // 카드 클릭 핸들러
  // =============================================
  const handleCoinClick = (coin) => {
    if (selectedCoin === coin) {
      setSelectedCoin(null); // 같은 코인 다시 클릭 → 차트 닫기
    } else {
      setSelectedCoin(coin);
      // 차트 영역으로 부드럽게 스크롤
      setTimeout(() => chartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  };

  const tvSymbol = selectedCoin ? `BINANCE:${selectedCoin}USDT` : null;

  // =============================================
  // 렌더링
  // =============================================
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

      {/* ===== 페이지 헤더 ===== */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <SectionTitle>김치프리미엄</SectionTitle>
          {kimchiData && (
            <p style={{ margin: '0.2rem 0 0', color: '#718096', fontSize: '0.82rem' }}>
              1 USD = <strong style={{ color: '#e2e8f0' }}>{kimchiData.usdkrw?.toLocaleString()} KRW</strong>
              &nbsp;·&nbsp; 총 {sortedCoins.length}개 코인 (시가총액순)
              &nbsp;·&nbsp; 코인 클릭 → 차트 보기
            </p>
          )}
        </div>
        <button onClick={fetchKimchi} style={refreshBtnStyle}>새로고침</button>
      </div>

      {/* ===== 에러 메시지 ===== */}
      {error && <ErrorBox msg={error} />}

      {/* ===== 로딩 중 ===== */}
      {loading && (
        <div style={{
          padding: '3rem',
          textAlign: 'center',
          color: '#718096',
          backgroundColor: '#1a202c',
          borderRadius: '12px',
          fontSize: '0.9rem',
        }}>
          김치프리미엄 계산 중... (첫 로딩은 10~20초 걸릴 수 있어)
        </div>
      )}

      {/* ===== PremiumList: 코인 카드 그리드 + 더보기 버튼 ===== */}
      {/* 역할: 코인 목록을 카드 형태로 보여주는 핵심 영역이야.              */}
      {/*       showAll 상태에 따라 15개 or 전체를 보여주고,                  */}
      {/*       "더보기" 버튼으로 토글할 수 있어.                             */}
      {!loading && kimchiData && (
        <PremiumList
          coins={visibleCoins}
          totalCount={sortedCoins.length}
          showAll={showAll}
          onToggleShowAll={() => setShowAll(prev => !prev)}
          selectedCoin={selectedCoin}
          onCoinClick={handleCoinClick}
        />
      )}

      {/* ===== ChartPanel: 클릭 시 나타나는 차트 영역 ===== */}
      {/* 역할: selectedCoin에 값이 생기면 이 패널이 렌더링돼.              */}
      {/*       TradingView 위젯을 750px 높이로 꽉 채워서 보여줘.            */}
      {selectedCoin && (
        <ChartPanel
          ref={chartRef}
          coin={selectedCoin}
          symbol={tvSymbol}
          interval={chartInterval}
          onIntervalChange={setChartInterval}
          intervals={CHART_INTERVALS}
          onClose={() => setSelectedCoin(null)}
        />
      )}

    </div>
  );
}


// =============================================
// PremiumList 컴포넌트
// =============================================
// 역할: 코인 카드 그리드를 담당해.
//   - coins       : 화면에 표시할 코인 배열 (이미 정렬·슬라이스된 상태)
//   - totalCount  : 전체 코인 수 (더보기 버튼 텍스트에 사용)
//   - showAll     : 전체 보기 여부
//   - onToggleShowAll : 더보기 버튼 클릭 핸들러
//   - selectedCoin : 선택된 코인 (하이라이트용)
//   - onCoinClick  : 카드 클릭 핸들러
function PremiumList({ coins, totalCount, showAll, onToggleShowAll, selectedCoin, onCoinClick }) {
  const hiddenCount = totalCount - INITIAL_COUNT; // 더보기로 숨겨진 코인 수

  return (
    <section>
      {/* 코인 카드 그리드 */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(175px, 1fr))',
        gap: '0.75rem',               // 카드 사이 간격
        marginBottom: '1rem',
      }}>
        {coins.map(coin => (
          <KimchiCard
            key={coin.coin}
            coin={coin}
            isSelected={selectedCoin === coin.coin}
            onClick={() => onCoinClick(coin.coin)}
          />
        ))}
      </div>

      {/* 더보기 / 접기 버튼 */}
      {totalCount > INITIAL_COUNT && (
        <div style={{ textAlign: 'center' }}>
          <button onClick={onToggleShowAll} style={showMoreBtnStyle}>
            {showAll
              ? '▲ 접기'
              : `▼ 더보기 (나머지 ${hiddenCount}개 코인 보기)`}
          </button>
        </div>
      )}
    </section>
  );
}


// =============================================
// ChartPanel 컴포넌트
// =============================================
// 역할: 코인 카드를 클릭했을 때 나타나는 차트 패널이야.
//   - coin      : 선택된 코인 심볼 (예: "BTC")
//   - symbol    : TradingView 심볼 (예: "BINANCE:BTCUSDT")
//   - interval  : 봉 단위 (예: "D")
//   - onClose   : 닫기 버튼 클릭 핸들러
// React.forwardRef를 써야 부모에서 ref(스크롤용)를 연결할 수 있어.
const ChartPanel = React.forwardRef(function ChartPanel(
  { coin, symbol, interval, onIntervalChange, intervals, onClose },
  ref
) {
  return (
    // overflow: hidden + borderRadius로 차트가 카드 모서리를 삐져나오지 않게
    <div
      ref={ref}
      style={{
        backgroundColor: '#1a202c',
        borderRadius: '14px',
        border: '1px solid #3182ce',
        overflow: 'hidden',   // 차트가 안에 꽉 차면서 둥근 모서리 유지
      }}
    >
      {/* 차트 헤더 — 패딩은 헤더 영역만 적용 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1rem 1.25rem',   // 헤더에만 좌우 패딩
        flexWrap: 'wrap',
        gap: '0.5rem',
      }}>
        {/* 왼쪽: 코인명 + 봉 단위 버튼 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 'bold', color: '#e2e8f0', fontSize: '1.1rem' }}>
            {coin} / USDT
          </span>
          <span style={{ fontSize: '0.78rem', color: '#4a5568' }}>
            Binance · TradingView
          </span>

          {/* 봉 단위 선택 버튼들 */}
          <div style={{ display: 'flex', gap: '0.3rem' }}>
            {intervals.map(iv => (
              <button
                key={iv.value}
                onClick={() => onIntervalChange(iv.value)}
                style={{
                  padding: '0.25rem 0.65rem',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  backgroundColor: interval === iv.value ? '#553c9a' : '#2d3748',
                  color: interval === iv.value ? '#e9d8fd' : '#718096',
                  transition: 'background-color 0.15s',
                }}
              >
                {iv.label}
              </button>
            ))}
          </div>
        </div>

        {/* 닫기 버튼 */}
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: '1px solid #4a5568',
            color: '#718096',
            borderRadius: '6px',
            cursor: 'pointer',
            padding: '0.25rem 0.9rem',
            fontSize: '0.8rem',
            transition: 'border-color 0.15s, color 0.15s',
          }}
        >
          ✕ 닫기
        </button>
      </div>

      {/* 차트 — 좌우 패딩 없이 카드 끝까지 꽉 차게 */}
      <TradingViewWidget symbol={symbol} interval={interval} height={800} />

      {/* 출처 표기 — 하단 우측 */}
      <p style={{ fontSize: '0.7rem', color: '#4a5568', margin: '0', padding: '6px 1.25rem 10px', textAlign: 'right' }}>
        Powered by TradingView · {coin}/USDT (Binance) 기준
      </p>
    </div>
  );
});


// =============================================
// KimchiCard 컴포넌트
// =============================================
// 역할: 코인 하나의 김치프리미엄 정보를 카드 형태로 보여줘.
//   isSelected가 true면 파란 테두리 + 크기 확대 효과를 줘.
function KimchiCard({ coin, isSelected, onClick }) {
  const isPositive = coin.premium_pct >= 0;
  const color      = isPositive ? '#48bb78' : '#fc8181';  // 양수=초록, 음수=빨강

  return (
    <div
      onClick={onClick}
      title={`${coin.coin} 차트 ${isSelected ? '닫기' : '보기'}`}
      style={{
        backgroundColor: isSelected ? '#1e2d3d' : '#1a202c',
        borderRadius: '10px',
        padding: '1rem 1rem 0.85rem',     // 위아래 여유 공간
        borderTop: `3px solid ${color}`,
        cursor: 'pointer',
        outline: isSelected ? '2px solid #3182ce' : 'none',
        outlineOffset: '1px',
        transition: 'background-color 0.15s, transform 0.12s',
        transform: isSelected ? 'scale(1.025)' : 'scale(1)',
        userSelect: 'none',
      }}
    >
      {/* 코인 심볼 + 프리미엄 % */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '5px' }}>
        <span style={{ fontWeight: 'bold', color: '#e2e8f0', fontSize: '0.95rem' }}>
          {coin.coin}
        </span>
        <span style={{ color, fontWeight: 'bold', fontSize: '0.9rem' }}>
          {isPositive ? '+' : ''}{coin.premium_pct}%
        </span>
      </div>

      {/* 한국어 이름 */}
      <div style={{ color: '#718096', fontSize: '0.73rem', marginBottom: '5px' }}>
        {coin.name}
      </div>

      {/* 가격 정보 */}
      <div style={{ fontSize: '0.68rem', color: '#4a5568', lineHeight: '1.5' }}>
        <div>업비트 {coin.upbit_krw?.toLocaleString()}원</div>
        <div>바이낸스 ${coin.binance_usd}</div>
      </div>

      {/* 선택 중 표시 */}
      {isSelected && (
        <div style={{ marginTop: '7px', fontSize: '0.65rem', color: '#63b3ed', fontWeight: 'bold' }}>
          ▼ 차트 보는 중
        </div>
      )}
    </div>
  );
}


// =============================================
// 작은 유틸 컴포넌트들
// =============================================

function SectionTitle({ children }) {
  return (
    <h2 style={{
      margin: '0 0 0.2rem',
      color: '#e2e8f0',
      fontSize: '0.9rem',
      fontWeight: '700',
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      borderLeft: '3px solid #3182ce',
      paddingLeft: '0.6rem',
    }}>
      {children}
    </h2>
  );
}

function ErrorBox({ msg }) {
  return (
    <div style={{
      padding: '1rem',
      backgroundColor: '#2d1515',
      border: '1px solid #c53030',
      borderRadius: '8px',
      color: '#fc8181',
      fontSize: '0.9rem',
    }}>
      {msg}
    </div>
  );
}


// =============================================
// 버튼 스타일 상수들
// =============================================

// 새로고침 버튼 — 우측 상단의 작은 버튼
const refreshBtnStyle = {
  padding: '0.35rem 0.85rem',
  backgroundColor: 'transparent',
  color: '#718096',
  border: '1px solid #4a5568',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '0.8rem',
  flexShrink: 0,
};

// 더보기 버튼 — 카드 그리드 아래 중앙에 위치
const showMoreBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.4rem',
  padding: '0.6rem 2.5rem',
  backgroundColor: '#2d3748',
  color: '#a0aec0',
  border: '1px solid #4a5568',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '0.85rem',
  fontWeight: '600',
  letterSpacing: '0.02em',
  transition: 'background-color 0.2s, color 0.2s, border-color 0.2s',
  // hover 효과는 CSS-in-JS로는 어려워서 onMouseEnter/Leave로 처리해.
  // 지금은 심플하게 두고 나중에 필요하면 추가해줄게.
};
